import { createInstance, type i18n as I18n, type Resource } from 'i18next'
import { useMemo, type ReactNode } from 'react'
import { I18nextProvider, initReactI18next } from 'react-i18next'
import {
  supportedLngs,
  fallbackLng,
  defaultNS,
  type SupportedLanguage,
  type Namespace
} from '#app/utils/i18n/i18n.shared'

interface I18nProviderProps {
  children: ReactNode
  lng: SupportedLanguage
  resources: Resource
  namespaces?: readonly Namespace[]
}

// Create a singleton cache for i18n instances to prevent re-initialization
const instanceCache = new Map<string, I18n>()

function getOrCreateI18nInstance(
  lng: SupportedLanguage,
  resources: Resource,
  namespaces?: readonly Namespace[]
): I18n {
  // Create a cache key based on language and namespaces
  const cacheKey = `${lng}-${namespaces?.join(',') || 'all'}`

  // Return cached instance if available and has the same resources
  const cached = instanceCache.get(cacheKey)
  if (cached && cached.isInitialized) {
    // Update resources if they've changed
    Object.keys(resources[lng] || {}).forEach(ns => {
      if (resources[lng]?.[ns] && !cached.hasResourceBundle(lng, ns)) {
        cached.addResourceBundle(lng, ns, resources[lng][ns], true, true)
      }
    })
    return cached
  }

  // Create new instance
  const instance = createInstance()

  // Initialize synchronously for SSR and initial client render
  // We use init with initImmediate: false to ensure synchronous initialization
  void instance.use(initReactI18next).init({
    lng,
    fallbackLng,
    supportedLngs: supportedLngs as unknown as string[],
    defaultNS,
    ns: namespaces || Object.keys(resources[lng] || {}),
    resources,
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false, // Disable suspense to prevent flashing
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
      transEmptyNodeValue: '', // Return empty string for missing translations instead of key
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i'],
    },
    initImmediate: false, // Synchronous initialization
    preload: false, // Don't preload languages as we provide resources directly
    load: 'currentOnly', // Only load current language to speed up initialization
    cleanCode: true,
    nonExplicitSupportedLngs: true,
    returnEmptyString: false, // Return the key if translation is missing (for debugging)
    fallbackNS: false, // Don't fallback to other namespaces
    debug: false, // Disable debug mode in production
    keySeparator: '.', // Use dot notation for nested keys
    nsSeparator: ':', // Use colon for namespace separation
  })

  // The instance is now initialized synchronously with initImmediate: false

  // Cache the instance
  instanceCache.set(cacheKey, instance)

  return instance
}

export function I18nProviderOptimized({
  children,
  lng,
  resources,
  namespaces
}: I18nProviderProps) {
  // Create or get cached i18n instance
  const i18n = useMemo(
    () => {
      const instance = getOrCreateI18nInstance(lng, resources, namespaces)

      // Ensure resources are loaded for the current language
      if (!instance.hasResourceBundle(lng, defaultNS)) {
        // Add resources for the current language if missing
        Object.keys(resources[lng] || {}).forEach(ns => {
          if (resources[lng]?.[ns]) {
            instance.addResourceBundle(lng, ns, resources[lng][ns], true, true)
          }
        })
      }

      return instance
    },
    [lng, resources, namespaces]
  )

  // Update language if it changes (e.g., user switches language)
  useMemo(() => {
    if (i18n.language !== lng && i18n.isInitialized) {
      void i18n.changeLanguage(lng)
    }
  }, [i18n, lng])

  return (
    <I18nextProvider i18n={i18n} defaultNS={defaultNS}>
      {children}
    </I18nextProvider>
  )
}